import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Skeleton } from '@/components/ui/misc'
import { useSessionStore } from '@/features/auth/sessionStore'
import { SessionReplacedScreen } from '@/features/auth/screens/SessionReplacedScreen'

/* Route guards.

   `status === 'unknown'` matters: on a cold load the session is restored from
   the refresh cookie asynchronously. Redirecting during that window would
   bounce every returning student to the login screen for a moment. */

function Gate({
  allow,
  redirectTo,
  children,
}: {
  /* An array rather than a single value, because exactly three routes — the
     exam, its result and its leaderboard — are reachable by two different
     kinds of principal. Everywhere else stays single-audience. */
  allow: readonly ('student' | 'admin' | 'guest')[]
  redirectTo: string
  children?: ReactNode
}): ReactNode {
  const status = useSessionStore((state) => state.status)
  const user = useSessionStore((state) => state.user)
  const guestSessionEnded = useSessionStore((state) => state.guestSessionEnded)
  const location = useLocation()

  if (status === 'replaced') return <SessionReplacedScreen />

  if (status === 'unknown') {
    return (
      <div style={{ padding: 'var(--s-8)', maxWidth: '640px', margin: '0 auto' }} aria-busy="true">
        <span className="visually-hidden">Restoring your session…</span>
        <Skeleton height="32px" />
        <div style={{ height: 'var(--s-4)' }} />
        <Skeleton height="120px" />
      </div>
    )
  }

  if (status !== 'authenticated' || user === null) {
    /* A guest has nowhere to be sent back to: there is no sign-in that would
       produce a guest session, only the link they came from. Their own link
       token is the only route home, and it is on the session that just went
       away — so they go to the landing page with an explanation rather than to
       a login form that cannot help them.

       The test is `guestSessionEnded`, not the shape of `allow`. Every route
       that admits a guest admits a student on the same path — the exam, its
       result, its leaderboard are one screen serving both — so there is no
       guest-only `allow` to key off, and asking the route who this was can
       only ever answer "either". The store knows, because it is told at the
       moment the session ends and while the token kind is still in hand. */
    if (allow.includes('guest') && guestSessionEnded) {
      return <Navigate to="/e/expired" replace />
    }
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${redirectTo}?next=${next}`} replace />
  }

  // An admin token is not a student token. Sending an admin to the student
  // portal (or the reverse) would fail confusingly at the first API call.
  if (!allow.includes(user.user_type as 'student' | 'admin' | 'guest')) {
    if (allow.includes('admin')) return <Navigate to="/admin/login" replace />

    /* A GUEST landing here has NOT lost anything. Their session is alive — it
       simply does not open the portal, and the header hands them the public
       site the moment they finish their paper, so browsing the catalogue and
       clicking a series is the expected next thing they do. Telling them
       "your exam session has ended" at that point is both wrong and alarming:
       nothing ended, and the sentence reads as though their submitted paper
       went with it.

       On these routes a guest is a visitor, and gets what a visitor gets — the
       sign-in that would actually open the page, with `next` so it lands where
       they were going. `/e/expired` stays for the exam routes, where a missing
       session is the one thing it can mean. */
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${redirectTo}?next=${next}`} replace />
  }

  return <>{children ?? <Outlet />}</>
}

export function ProtectedRoute({ children }: { children?: ReactNode }): ReactNode {
  return (
    <Gate allow={['student']} redirectTo="/login">
      {children}
    </Gate>
  )
}

export function AdminRoute({ children }: { children?: ReactNode }): ReactNode {
  return (
    <Gate allow={['admin']} redirectTo="/admin/login">
      {children}
    </Gate>
  )
}

/* The only routes a guest token opens.

   A student reaches these too, on their own attempts — the backend decides
   whose attempt is whose, and answers a guest asking about somebody else's
   with a 404. This guard's job is narrower: keep a guest token out of the
   dashboard, the profile and the catalog, where it would produce a wall of
   401s and a session-lost redirect instead of an explanation. */
export function ExamRoute({ children }: { children?: ReactNode }): ReactNode {
  return (
    <Gate allow={['student', 'guest']} redirectTo="/login">
      {children}
    </Gate>
  )
}

/* Sends an already-signed-in user away from the login and signup screens —
   but only from the portal they are signed in to.

   The `audience` distinction matters: an admin and a student are separate
   accounts, and a signed-in student must still be able to reach
   /admin/login to sign in as an administrator. Redirecting on "authenticated"
   alone makes the admin portal unreachable without signing out first. */
export function PublicOnlyRoute({
  audience = 'student',
}: {
  audience?: 'student' | 'admin'
}): ReactNode {
  const status = useSessionStore((state) => state.status)
  const user = useSessionStore((state) => state.user)

  if (status === 'authenticated' && user !== null && user.user_type === audience) {
    return <Navigate to={audience === 'admin' ? '/admin' : '/dashboard'} replace />
  }
  return <Outlet />
}
